import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE from './apiBase';
import Swal from 'sweetalert2';
import {
  Building2, Users, Briefcase, Monitor, HeartPulse, GraduationCap,
  Plus, ArrowLeft, Eye, Edit, FileText
} from 'lucide-react';
import '../css/App.css';

const DepartmentConsent = ({ onBack }) => {
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newDept, setNewDept] = useState({ name: '', description: '', usage: 0, color: 'blue' });
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);

  const fetchDepartments = async () => {
    try {
      // const res = await axios.get(`${API_BASE}/departments`);
      const tenant_id = (window && window.USER && window.USER.tenant_id) ? Number(window.USER.tenant_id) : (Number(localStorage.getItem('tenant_id')) || null);
      const params = {};
      if (tenant_id) params.tenant_id = tenant_id;
      const res = await axios.get(`${API_BASE}/manager/departments`, { params });
      if (res.data && Array.isArray(res.data.departments)) {
        const mapped = res.data.departments.map(d => ({
          ...d,
          icon: <Building2 className="card-icon" />
        }));
        setDepartments(mapped);
        return;
      }
    } catch (err) {
      console.warn('Failed to load departments from server', err);
    }

    // fallback static data
    setDepartments([
      { id: 1, name: 'Human Resources', description: 'Consent for HR data access and employee records.', icon: <Users className="card-icon" />, usage: 78, color: 'blue' },
      { id: 2, name: 'Finance', description: 'Consent for financial data and payroll information.', icon: <Briefcase className="card-icon" />, usage: 65, color: 'green' },
      { id: 3, name: 'IT & Technology', description: 'Consent for IT systems access and digital records.', icon: <Monitor className="card-icon" />, usage: 90, color: 'orange' },
      { id: 4, name: 'Healthcare', description: 'Consent for medical records and health data access.', icon: <HeartPulse className="card-icon" />, usage: 55, color: 'red' },
      { id: 5, name: 'Education', description: 'Consent for academic records and training data.', icon: <GraduationCap className="card-icon" />, usage: 42, color: 'purple' },
    ]);
  };

  useEffect(() => { fetchDepartments(); }, []);

  const totalUsage = departments.reduce((sum, d) => sum + d.usage, 0);
  const avgUsage = Math.round(totalUsage / Math.max(1, departments.length));

  return (
    <div className="app-container">
      <div className="content-wrapper">

        {/* Header Section */}
        <div className="header-section">
          <h2 className="main-title">Departments</h2>

          <div className="button-group">
            <button
              className="btn-add-data"
              onClick={() => { setAdding(a => !a); setSelected(null); setEditing(false); }}
            >
              <Plus className="btn-icon" />
              {adding ? 'Cancel' : 'Add'}
            </button>

            <button className="btn-back" onClick={() => onBack && onBack()}>
              <ArrowLeft className="btn-icon" />
              Back
            </button>
          </div>
        </div>

        {/* Analytics Section */}
        {showAnalytics && (
          <div className="analytics-container">
            <div className="stats-grid">
              <div className="stat-card stat-blue">
                <div className="stat-icon-wrapper stat-icon-blue">
                  <span className="stat-emoji">📈</span>
                </div>
                <div className="stat-content">
                  <h3 className="stat-number">{totalUsage}</h3>
                  <p className="stat-label">Total Usage</p>
                </div>
              </div>

              <div className="stat-card stat-green">
                <div className="stat-icon-wrapper stat-icon-green">
                  <span className="stat-emoji">📊</span>
                </div>
                <div className="stat-content">
                  <h3 className="stat-number">{avgUsage}%</h3>
                  <p className="stat-label">Avg Usage</p>
                </div>
              </div>

              <div className="stat-card stat-orange">
                <div className="stat-icon-wrapper stat-icon-orange">
                  <span className="stat-emoji">🏢</span>
                </div>
                <div className="stat-content">
                  <h3 className="stat-number">{departments.length}</h3>
                  <p className="stat-label">Departments</p>
                </div>
              </div>
            </div>

            <div className="usage-statistics">
              <h3 className="usage-title">Usage Statistics</h3>
              <div className="usage-bars">
                {departments.map((dept) => (
                  <div key={dept.id} className="usage-row">
                    <div className="usage-label">
                      <div className={`usage-icon-wrapper icon-${dept.color}`}>
                        {dept.icon}
                      </div>
                      <span className="usage-name">{dept.name}</span>
                    </div>
                    <div className="usage-bar-container">
                      <div className="usage-bar-bg">
                        {/* <div
                          className={`usage-bar bar-${dept.color}`}
                          style={{ width: `${dept.usage}%` }}
                        ></div> */}
                      </div>
                    </div>
                  
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Add Department Slide-in Panel */}
        {adding && (
          <div style={{
            position: 'fixed', top: 0, right: 0, width: 380, maxWidth: '92%',
            height: '100%', background: '#ffffff',
            boxShadow: '-4px 0 16px rgba(0,0,0,0.12)',
            transition: 'transform 0.25s ease', zIndex: 1000,
            padding: 20, overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Add Department</h3>
              <button className="btn-back" onClick={() => {
                setAdding(false);
                setNewDept({ name: '', description: '', usage: 0, color: 'blue' });
              }}>Close</button>
            </div>

            <div style={{ marginBottom: 12 }}>
              <input
                className="w-full px-3 py-2 border rounded"
                placeholder="Department name"
                value={newDept.name}
                onChange={e => setNewDept({ ...newDept, name: e.target.value })}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <input
                className="w-full px-3 py-2 border rounded"
                placeholder="Description"
                value={newDept.description}
                onChange={e => setNewDept({ ...newDept, description: e.target.value })}
              />
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
             
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="px-3 py-2 bg-indigo-600 text-white rounded"
                onClick={async () => {
                  const name = (newDept.name || '').trim();
                  if (!name) {
                    return Swal.fire({
                      icon: 'warning',
                      title: 'Missing name',
                      text: 'Enter a department name',
                      confirmButtonText: 'OK',
                      customClass: { popup: 'swal-popup-custom' }
                    });
                  }
                  try {
                    const payload = {
                      name,
                      description: newDept.description || null,
                      usage: Number(newDept.usage) || 0,
                      color: newDept.color || 'blue'
                    };
                    // const res = await axios.post(`${API_BASE}/departments`, payload);
                    const tenant_id = (window && window.USER && window.USER.tenant_id) ? Number(window.USER.tenant_id) : (Number(localStorage.getItem('tenant_id')) || null);
                    const body = { ...payload };
                    if (tenant_id) body.tenant_id = tenant_id;
                    const res = await axios.post(`${API_BASE}/manager/departments`, body);
                    const success = Boolean(res?.data?.success) || (res?.status >= 200 && res?.status < 300);
                    if (success) {
                      try {
                        await fetchDepartments();
                      } catch (refreshErr) {
                        console.warn('Department created, but failed to refresh list', refreshErr);
                      }
                      try { window.dispatchEvent(new CustomEvent('departmentsUpdated', { detail: { action: 'create', id: res?.data?.department?.id || null } })); } catch (e) {}
                      setNewDept({ name: '', description: '', usage: 0, color: 'blue' });
                      setAdding(false);
                      Swal.fire({
                        icon: 'success',
                        title: 'Created',
                        text: 'Department added successfully',
                        customClass: { popup: 'swal-popup-custom' }
                      });
                    } else {
                      const msg = res?.data?.error || res?.data?.detail || 'Failed to save department';
                      Swal.fire({
                        icon: 'error',
                        title: 'Save failed',
                        text: msg,
                        confirmButtonText: 'OK',
                        customClass: { popup: 'swal-popup-custom' }
                      });
                    }
                  } catch (err) {
                    console.error('Failed to save department', err);
                    const msg = err?.response?.data?.detail || err?.response?.data?.error || err?.message || 'Failed to save department to server';
                    Swal.fire({
                      icon: 'error',
                      title: 'Save failed',
                      text: msg,
                      confirmButtonText: 'OK',
                      customClass: { popup: 'swal-popup-custom' }
                    });
                  }
                }}
              >
                Create
              </button>
              <button
                className="px-3 py-2 bg-gray-200 rounded"
                onClick={() => { setAdding(false); setNewDept({ name: '', description: '', usage: 0, color: 'blue' }); }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Department Cards Grid */}
        <div className="cards-grid">
          {departments.map((dept) => (
            <div
              key={dept.id}
              className="consent-card cursor-pointer"
              onMouseEnter={() => setHoveredId(dept.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => { setSelected(dept); setEditing(false); }}
              style={{ position: 'relative' }}
            >
              {/* Hover action icons */}
              {hoveredId === dept.id && (
                <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 8, zIndex: 2 }}>
                  {/* VIEW */}
                  <button
                    style={{
                      background: 'transparent', border: 'none', padding: 10,
                      borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#2563eb';
                      e.currentTarget.querySelector('svg').style.color = '#ffffff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.querySelector('svg').style.color = '';
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelected(dept);
                      setEditing(false);
                    }}
                  >
                    <Eye size={18} />
                  </button>

                  {/* EDIT */}
                  <button
                    style={{
                      background: 'transparent', border: 'none', padding: 10,
                      borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#16a34a';
                      e.currentTarget.querySelector('svg').style.color = '#ffffff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.querySelector('svg').style.color = '';
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelected(dept);
                      setEditing(true);
                      setNewDept({
                        name: dept.name,
                        description: dept.description,
                        usage: dept.usage,
                        color: dept.color
                      });
                    }}
                  >
                    <Edit size={18} />
                  </button>
                </div>
              )}

              <div className="card-content">
                <div className={`card-icon-wrapper icon-bg-${dept.color}`}>
                  {dept.icon || <Building2 className="card-icon" />}
                </div>
                <h3 className="card-title">{dept.name}</h3>
                <p className="card-description">{dept.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* View / Edit Modal */}
        {selected && (
          <div
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100
            }}
            onClick={() => { setSelected(null); setEditing(false); }}
          >
            <div
              style={{ width: 640, maxWidth: '94%', background: '#fff', borderRadius: 8, padding: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <h4 style={{ margin: 0 }}>{selected.name}</h4>
                  <div className="text-xs text-gray-600">{selected.description}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {!editing ? (
                    <button
                      className="px-3 py-1 bg-indigo-600 text-white rounded"
                      onClick={() => {
                        setEditing(true);
                        setNewDept({
                          name: selected.name,
                          description: selected.description,
                          // usage: selected.usage,
                          // color: selected.color
                        });
                      }}
                    >
                      Edit
                    </button>
                  ) : (
                    <>
                      <button
                        className="px-3 py-1 bg-green-600 text-white rounded"
                        onClick={async () => {
                          const name = (newDept.name || '').trim();
                          if (!name) {
                            return Swal.fire({
                              icon: 'warning',
                              title: 'Missing name',
                              text: 'Enter a department name',
                              customClass: { popup: 'swal-popup-custom' }
                            });
                          }
                          const payload = {
                            name: newDept.name,
                            description: newDept.description,
                            // usage: Number(newDept.usage) || 0,
                            // color: newDept.color
                          };
                          try {
                            await axios.put(`${API_BASE}/manager/departments/${selected.id}`, payload);
                            await fetchDepartments();
                            try { window.dispatchEvent(new CustomEvent('departmentsUpdated', { detail: { action: 'update', id: selected.id } })); } catch (e) {}
                            Swal.fire({
                              icon: 'success',
                              title: 'Updated',
                              text: 'Department updated successfully',
                              customClass: { popup: 'swal-popup-custom' }
                            });
                          } catch (err) {
                            // fallback: update locally if API not available
                            setDepartments(prev =>
                              prev.map(d =>
                                d.id === selected.id
                                  ? { ...d, ...payload, icon: d.icon }
                                  : d
                              )
                            );
                            try { window.dispatchEvent(new CustomEvent('departmentsUpdated', { detail: { action: 'update', id: selected.id } })); } catch (e) {}
                            Swal.fire({
                              icon: 'warning',
                              title: 'Updated locally',
                              text: 'Saved on this device, but failed to sync with server: ' + (err?.response?.data?.detail || err?.message || ''),
                              customClass: { popup: 'swal-popup-custom' }
                            });
                          }
                          setSelected(null);
                          setEditing(false);
                        }}
                      >
                        Save
                      </button>
                      <button
                        className="px-3 py-1 bg-gray-200 rounded"
                        onClick={() => setEditing(false)}
                      >
                        Cancel
                      </button>
                    </>
                  )}

                  <button
                    className="px-3 py-1 bg-red-500 text-white rounded"
                    onClick={async () => {
                      const deptToDelete = selected;
                      // Close this view/edit modal first so the Swal confirm
                      // dialog isn't stacked behind it / hidden after it closes.
                      setSelected(null);
                      setEditing(false);

                      const result = await Swal.fire({
                        title: 'Delete Department',
                        text: `Delete "${deptToDelete.name}"? This cannot be undone.`,
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: 'Delete',
                        cancelButtonText: 'Cancel',
                        confirmButtonColor: '#ef4444',
                        customClass: { popup: 'swal-popup-custom' }
                      });

                      if (!result.isConfirmed) {
                        // reopen the modal if user cancels
                        setSelected(deptToDelete);
                        setEditing(false);
                        return;
                      }

                      try {
                        await axios.delete(`${API_BASE}/manager/departments/${deptToDelete.id}`);
                        await fetchDepartments();
                        try { window.dispatchEvent(new CustomEvent('departmentsUpdated', { detail: { action: 'delete', id: deptToDelete.id } })); } catch (e) {}
                        Swal.fire({
                          icon: 'success',
                          title: 'Deleted',
                          text: 'Department has been deleted successfully',
                          customClass: { popup: 'swal-popup-custom' }
                        });
                      } catch (err) {
                        setDepartments(prev => prev.filter(d => d.id !== deptToDelete.id));
                        try { window.dispatchEvent(new CustomEvent('departmentsUpdated', { detail: { action: 'delete', id: deptToDelete.id } })); } catch (e) {}
                        Swal.fire({
                          icon: 'warning',
                          title: 'Deleted locally',
                          text: 'Removed on this device, but failed to sync with server: ' + (err?.response?.data?.detail || err?.message || ''),
                          customClass: { popup: 'swal-popup-custom' }
                        });
                      }
                    }}
                  >
                    Delete
                  </button>

                  <button
                    className="px-3 py-1 bg-gray-200 rounded"
                    onClick={() => { setSelected(null); setEditing(false); }}
                  >
                    Close
                  </button>
                </div>
              </div>

              {!editing ? (
                <div>
                  <p>{selected.description}</p>
                  {/* <p><strong>Usage:</strong> {selected.usage}%</p>
                  <p><strong>Color:</strong> {selected.color}</p> */}
                </div>
              ) : (
                <div className="mt-3" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    placeholder="Department name"
                    value={newDept.name}
                    onChange={e => setNewDept({ ...newDept, name: e.target.value })}
                  />
                  <input
                    className="w-full px-3 py-2 border rounded"
                    placeholder="Description"
                    value={newDept.description}
                    onChange={e => setNewDept({ ...newDept, description: e.target.value })}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default DepartmentConsent;